import React from 'react';
import { Button } from '../../ui/button';
import { 
  ChevronUp, ChevronDown, ChevronRight, AlertCircle, Check, Trash2, 
  FileText, ArrowRight, Scissors, Edit, Lock, Eye, Loader2
} from 'lucide-react';

export default function LoteCardExpandido({
  lote,
  ofId,
  aprovando,
  onAprovar,
  onEliminar,
  onAvancar,
  onDividir,
  onGerarPDF,
  onEditarQuantidade,
}) {
  const isArr = lote.categoria_codigo === 'ARR' || lote.origem === 'arranjo';
  const isAprovado = lote.status === 'aprovado';
  const corCategoria = lote.categoria_cor || (isArr ? '#dc2626' : null);
  const dynamicStyle = isAprovado && corCategoria
    ? { borderLeft: `4px solid ${corCategoria}` }
    : {};
  const fontes = lote.fontes_arranjo || [];  // eslint-disable-line no-unused-vars
  const hasRastreabilidade = isArr && fontes.length > 0;  // eslint-disable-line no-unused-vars
  return (
    <div 
      className={`p-2 rounded-lg text-sm ${
        isArr ? 'bg-rose-50 border-2 border-rose-300' :
        lote.status === 'pendente' ? 'bg-amber-50 border border-amber-200' :
        lote.status === 'aprovado' ? 'bg-white border border-stone-200 shadow-sm' :
        'bg-stone-50 border border-stone-200'
      }`}
      style={dynamicStyle}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
          {isArr && (
            <span className="font-mono text-[10px] font-bold bg-rose-600 text-white px-1.5 py-0.5 rounded uppercase tracking-wide flex-shrink-0">
              ARR
            </span>
          )}
          <span className={`font-mono font-bold flex-shrink-0 ${isArr ? 'text-rose-700' : ''}`}>
            {lote.codigo_lote || `L${lote.numero_lote}`}
          </span>
          <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
            isArr ? 'bg-rose-200 text-rose-800' :
            lote.status === 'pendente' ? 'bg-amber-100 text-amber-700' :
            'bg-stone-100 text-stone-700'
          }`}
          style={isAprovado && !isArr && corCategoria ? { backgroundColor: `${corCategoria}20`, color: corCategoria } : {}}
          >
            {isArr ? 'Arranjo' : lote.status}
          </span>
          {lote.quantidade === 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-stone-200 text-stone-600 flex-shrink-0">
              Esgotado
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          <span className="text-stone-600 text-xs whitespace-nowrap">
            {lote.quantidade_produzida || 0}/{lote.quantidade_ajustada || lote.quantidade} pcs
            {lote.quantidade_ajustada && lote.quantidade_ajustada !== lote.quantidade && (
              <span className="ml-1 text-[10px] text-sky-600" title={`Original: ${lote.quantidade} | Ajustada: ${lote.quantidade_ajustada}`}>
                (ajust.)
              </span>
            )}
          </span>
          {lote.pode_editar_quantidade ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditarQuantidade(lote);
              }}
              className="p-1 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 hover:text-blue-700 transition-colors border border-blue-200 flex-shrink-0"
              title="Editar quantidade do lote"
            >
              <Edit className="h-3.5 w-3.5" />
            </button>
          ) : (
            (lote.quantidade_produzida > 0 || (lote.resumo_operacoes?.concluidas > 0 || lote.resumo_operacoes?.em_curso > 0)) && (
              <span 
                className="p-1 rounded-lg bg-stone-100 text-stone-400 cursor-not-allowed border border-stone-200 flex-shrink-0"
                title="Não é possível editar - já existe produção neste lote"
              >
                <Lock className="h-3.5 w-3.5" />
              </span>
            )
          )}
          {lote.origem && lote.origem !== 'novo' && lote.origem !== 'arranjo' && (
            <span className="text-xs text-purple-600 flex-shrink-0" title={`Origem: ${lote.origem}`}>
              {lote.origem === 'uniao' ? '⊕' : lote.origem === 'divisao' ? '✂' : '↑'}
            </span>
          )}
        </div>
      </div>
      
      {/* Estado das operações do lote */}
      {lote.resumo_operacoes && lote.resumo_operacoes.total > 0 ? (
        <div className="mt-2 pt-2 border-t border-dashed">
          <div className="flex items-center justify-between text-xs">
            <span className="text-stone-500">Operações ({lote.resumo_operacoes.total}):</span>
            <div className="flex items-center gap-2">
              {lote.resumo_operacoes.concluidas > 0 && (
                <span className="text-green-600">
                  {lote.resumo_operacoes.concluidas} concluídas
                </span>
              )}
              {lote.resumo_operacoes.em_curso > 0 && (
                <span className="text-blue-600">
                  {lote.resumo_operacoes.em_curso} em curso
                </span>
              )}
              {lote.resumo_operacoes.pendentes > 0 && (
                <span className="text-stone-400">
                  {lote.resumo_operacoes.pendentes} pendentes
                </span>
              )}
            </div>
          </div>
          {/* Mini barra de progresso das operações */}
          <div className="mt-1 h-1.5 bg-stone-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-green-500 transition-all"
              style={{ width: `${lote.resumo_operacoes.percentagem}%` }}
            />
          </div>
          <p className="text-xs text-stone-400 mt-0.5 text-right">
            {lote.resumo_operacoes.percentagem}% das operações
          </p>
        </div>
      ) : (
        <div className="mt-2 pt-2 border-t border-dashed">
          <div className="flex items-center gap-2 text-xs text-red-600 animate-pulse">
            <AlertCircle className="h-3.5 w-3.5" />
            <span className="font-medium">Sem operações atribuídas</span>
          </div>
        </div>
      )}
      
      {/* Vista Rastreabilidade — removida a pedido do user (manter o card limpo) */}

      {/* Botões de Acção do Lote Aprovado */}
      {lote.status === 'aprovado' && lote.quantidade > 0 && (
        <div className="mt-2 pt-2 border-t flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            className="text-xs h-7 border-sky-300 text-sky-700 hover:bg-sky-50"
            onClick={(e) => {
              e.stopPropagation();
              window.location.href = `/gestor/ofs?of=${ofId}&tab=acompanhar&lote=${lote.id}`;
            }}
            title="Acompanhar produção do lote"
          >
            <Eye className="h-3 w-3 mr-1" />
            Acompanhar
          </Button>
          {!isArr && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-7 border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={(e) => {
                e.stopPropagation();
                onAvancar(lote);
              }}
            >
              <ArrowRight className="h-3 w-3 mr-1" />
              Avançar
            </Button>
          )}
          {!isArr && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-7 border-purple-300 text-purple-700 hover:bg-purple-50"
              onClick={(e) => {
                e.stopPropagation();
                onDividir(lote);
              }}
            >
              <Scissors className="h-3 w-3 mr-1" />
              Dividir
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="text-xs h-7"
            onClick={(e) => {
              e.stopPropagation();
              onGerarPDF(lote);
            }}
          >
            <FileText className="h-3 w-3 mr-1" />
            PDF
          </Button>
          {/* Botão Terceirizar removido daqui — fluxo agora vive na Aba Terceirização (Gestão de Anomalias) */}
        </div>
      )}
      
      {/* Botões de Acção do Lote Pendente */}
      {lote.status === 'pendente' && (
        <div className="mt-2 pt-2 border-t flex gap-2">
          <Button
            size="sm"
            className="text-xs h-7 bg-green-600 hover:bg-green-700"
            onClick={(e) => {
              e.stopPropagation();
              onAprovar(lote.id);
            }}
            disabled={aprovando === lote.id}
          >
            {aprovando === lote.id ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <Check className="h-3 w-3 mr-1" />
            )}
            Aprovar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-xs h-7 text-red-600 hover:bg-red-50"
            onClick={(e) => {
              e.stopPropagation();
              onEliminar(lote.id);
            }}
          >
            <Trash2 className="h-3 w-3 mr-1" />
            Eliminar
          </Button>
        </div>
      )}
    </div>
  );
}
